import type { DocumentModel } from "@bindery/shared/corpus/document";
import { isEnglishStopWord } from "@bindery/shared/reader/stop-words";
import { normalizeSelection } from "@bindery/convex-retells/model/selection";
import { foldQuote } from "@bindery/convex-retells/model/wordGloss";

export interface LookupSpan {
  readonly start: number;
  readonly end: number;
}

/**
 * Whether the retelling offers Look up on a selection at all. The reader has
 * no dictionary yet, so in a chapter without links every lookup would answer
 * "No dictionary entry found".
 */
export const offersSelectionLookup = (model: DocumentModel): boolean =>
  model.contextLinks.length > 0;

/**
 * The span a raw selection in the retelling offers a lookup for: snapped out
 * to whole tokens, or, when it touches a context link, that link's complete
 * phrase, so the link's background is what gets looked up. A selection made
 * only of function words is dropped. `null` means no lookup is offered.
 */
export function lookupSpan(
  model: DocumentModel,
  selStart: number,
  selEnd: number,
  isStopWord: (word: string) => boolean = isEnglishStopWord,
): LookupSpan | null {
  const snapped = normalizeSelection(model, selStart, selEnd);
  if (!snapped) return null;
  const { start, end } = snapped;
  const overlap = (l: { start: number; end: number }): number =>
    Math.min(end, l.end) - Math.max(start, l.start);
  // The link the selection overlaps most, not the union of every link it
  // touches: the lookup attaches context only to an exact link span.
  const link = model.contextLinks
    .filter((l) => overlap(l) > 0)
    .reduce<{ start: number; end: number } | null>(
      (best, l) => (best === null || overlap(l) > overlap(best) ? l : best),
      null,
    );
  if (link !== null) return { start: link.start, end: link.end };
  const words = model.tokens.filter((t) => t.start >= start && t.end <= end);
  const stop = (t: { start: number; end: number }): boolean =>
    isStopWord(foldQuote(model.text.slice(t.start, t.end)));
  return words.every(stop) ? null : { start, end };
}
